"use client"

import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import type { JobListing, LexicalContent, LexicalNode } from "@/lib/cms"
import { jobSlug, cmsLocale } from "@/lib/cms"
import { safeUrl } from "@/lib/url"
import { careersKeys as k } from "@/i18n/careers-keys"
import { FaqSection } from "@/components/ui/faq-section"
import { blobMediaUrl } from "@/lib/blob-url"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

/* Related-role title rendered inside an `otherJobs.map(...)` — a hook can't run
 * per loop iteration, so the editable region registers from this child (one
 * `useEditRegion` per mounted title), mirroring FAQ's `FaqAnswer`. */
export function OtherJobTitle({
  id,
  title,
  editLocale,
  className,
}: {
  id: string
  title: string
  editLocale: string
  className: string
}) {
  const ref = useEditRegion<HTMLHeadingElement>(
    id
      ? {
          collection: "job-listings",
          id,
          field: "title",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  return (
    <h3 ref={ref} className={className}>
      {title}
    </h3>
  )
}

/* ───────────────────── Icons ───────────────────── */

function ArrowRightIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  )
}

/* ───────────────────── Lexical renderer ───────────────────── */

// A Lexical tree from Payload is always acyclic and only a handful of levels
// deep. If recursion ever passes this depth the node graph is cyclic/corrupt —
// throw a DISTINCT, loud error rather than let it overflow into an unactionable
// "Maximum call stack size exceeded" (which we drop as third-party Sentry-replay
// noise; see instrumentation-client.ts). Keeps that message filter from ever
// masking a real recursion in this client renderer.
const MAX_LEXICAL_DEPTH = 100

function renderLexicalChildren(
  children: LexicalNode[],
  depth = 0
): React.ReactNode[] {
  if (depth > MAX_LEXICAL_DEPTH) {
    throw new Error(
      `renderLexicalChildren: Lexical tree exceeded ${MAX_LEXICAL_DEPTH} levels — cyclic or corrupt CMS node`
    )
  }
  return children.map((node, i) => {
    if (node.type === "text") {
      const text = node.text ?? ""
      if (!text) return null
      // CMS authors can lose the connecting whitespace when toggling bold/italic
      // mid-sentence (e.g. "As our" + bold "Senior Product Engineer"). Restore
      // a single space at format boundaries when both sides are clearly multi-
      // word fragments and neither carries the space already.
      const prev = children[i - 1]
      const needsLeadingSpace =
        prev?.type === "text" &&
        typeof prev.text === "string" &&
        prev.text.length > 0 &&
        (prev.format ?? 0) !== (node.format ?? 0) &&
        !/\s$/.test(prev.text) &&
        // Don't insert before a closing punctuation/quote that hugs the prior word.
        !/^[,.:;!?')\]}"]/.test(text) &&
        // Don't insert after an opening punctuation/quote that hugs the next word.
        !/[(\[{"']$/.test(prev.text) &&
        (prev.text.includes(" ") || text.includes(" "))
      const display = needsLeadingSpace ? " " + text : text
      let el: React.ReactNode = display
      if (node.format && node.format & 1) el = <strong key={i}>{el}</strong>
      if (node.format && node.format & 2) el = <em key={i}>{el}</em>
      return <span key={i}>{el}</span>
    }
    if (node.type === "link") {
      const href = node.fields?.url ?? node.url ?? "#"
      return (
        <a
          key={i}
          href={href}
          className="text-aubergine-700 hover:text-aubergine-900 underline underline-offset-2 transition-colors"
          target={href.startsWith("http") ? "_blank" : undefined}
          rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        >
          {node.children
            ? renderLexicalChildren(node.children, depth + 1)
            : null}
        </a>
      )
    }
    if (node.type === "linebreak") return <br key={i} />
    if (node.type === "listitem") {
      return (
        <li key={i} className="text-base leading-[1.6]">
          {node.children
            ? renderLexicalChildren(node.children, depth + 1)
            : null}
        </li>
      )
    }
    if (node.type === "list") {
      const items = node.children
        ? renderLexicalChildren(node.children, depth + 1)
        : null
      return node.listType === "number" ? (
        <ol key={i} className="mb-2 list-decimal space-y-2 pl-6">
          {items}
        </ol>
      ) : (
        <ul key={i} className="mb-2 list-disc space-y-2 pl-6">
          {items}
        </ul>
      )
    }
    return (
      <span key={i}>
        {node.children ? renderLexicalChildren(node.children, depth + 1) : null}
      </span>
    )
  })
}

function RenderLexical({ content }: { content: LexicalContent }) {
  return (
    <>
      {content.root.children?.map((node, i) => {
        const kids = node.children ? renderLexicalChildren(node.children) : null
        const isEmpty =
          !kids || (Array.isArray(kids) && kids.every((k) => k === null))
        switch (node.type) {
          case "heading": {
            if (isEmpty) return null
            const Tag =
              (node.tag as "h1" | "h2" | "h3" | "h4" | "h5" | "h6") || "h3"
            const headingType = {
              h1: "type-headline-lg",
              h2: "type-headline-md",
              h3: "type-headline-sm",
              h4: "type-title-lg",
              h5: "type-title-md",
              h6: "type-title-sm",
            }[Tag]
            return (
              <Tag
                key={i}
                className={`${headingType} text-foreground mt-12 mb-4 font-normal first:mt-0`}
              >
                {kids}
              </Tag>
            )
          }
          case "paragraph":
            if (isEmpty) return null
            return (
              <p
                key={i}
                className="text-foreground mb-4 text-base leading-[1.6]"
              >
                {kids}
              </p>
            )
          case "list":
            return node.listType === "number" ? (
              <ol
                key={i}
                className="text-foreground mb-4 list-decimal space-y-2 pl-6"
              >
                {kids}
              </ol>
            ) : (
              <ul
                key={i}
                className="text-foreground mb-4 list-disc space-y-2 pl-6"
              >
                {kids}
              </ul>
            )
          case "listitem":
            return (
              <li key={i} className="text-base leading-[1.6]">
                {kids}
              </li>
            )
          case "quote":
            return (
              <blockquote
                key={i}
                className="border-aubergine-200 my-6 border-l-4 pl-4 text-neutral-600 italic"
              >
                {kids}
              </blockquote>
            )
          default:
            return kids ? <div key={i}>{kids}</div> : null
        }
      })}
    </>
  )
}

/* ───────────────────── Page ───────────────────── */

export function JobDetailClient({
  job,
  otherJobs,
  initialFaqs,
}: {
  job: JobListing
  otherJobs: JobListing[]
  initialFaqs?: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
}) {
  const t = useTranslations("careers")
  const lp = useLocalePath()
  const locale = useLocale()
  const editLocale = cmsLocale(locale, useCountry().code)
  const titleRef = useEditRegion<HTMLHeadingElement>(
    job.id
      ? // no-raw-ids: Payload doc id for the inline-editor binding, not rendered
        {
          collection: "job-listings",
          id: job.id,
          field: "title",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const rawApplyUrl = safeUrl(job.applicationUrl)
  const applyUrl =
    rawApplyUrl !== "#"
      ? rawApplyUrl
      : // no-raw-ids: Needle ATS open-application form URL — id lives in the href, label is the link text
        "https://jobs.needle.so/aheadhealth/6bfb8884-ad8f-4cef-a4c3-83b19e581045?view=application"

  const translatedType =
    job.type === "Full-time"
      ? t("jobTypeFullTime" as never)
      : job.type === "Part-time"
        ? t("jobTypePartTime" as never)
        : job.type

  return (
    <>
      {/* ─── Hero ─── */}
      <section className="px-6 pt-12 pb-12 md:pt-20">
        <div className="mx-auto max-w-[1100px]">
          <p
            className="type-label-md text-foreground mb-6 font-mono font-medium uppercase"
            style={{ letterSpacing: "0.48px" }}
          >
            {t(k.openRolesLabel)}
          </p>

          <div className="flex flex-col gap-8 md:flex-row md:items-end md:gap-16">
            {/* Title */}
            <div className="min-w-0 flex-1">
              <h1
                ref={titleRef}
                className="type-display-lg text-foreground max-w-[545px] text-4xl leading-[0.9] font-normal sm:text-5xl"
              >
                {job.title}
              </h1>
            </div>

            {/* Metadata + apply */}
            <div className="flex flex-shrink-0 flex-wrap items-end gap-6 md:gap-8">
              <div className="flex flex-col gap-1">
                <span
                  className="type-label-md text-foreground font-mono font-medium uppercase"
                  style={{ letterSpacing: "0.48px" }}
                >
                  {t("metaLocation" as never)}
                </span>
                <span className="type-title-md text-foreground">
                  {job.location}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span
                  className="type-label-md text-foreground font-mono font-medium uppercase"
                  style={{ letterSpacing: "0.48px" }}
                >
                  {t("metaEmployment" as never)}
                </span>
                <span className="type-title-md text-foreground">
                  {translatedType}
                </span>
              </div>
              <a
                href={applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="type-label-md bg-aubergine-800 hover:bg-aubergine-900 inline-flex items-center justify-center rounded-lg px-4 py-2 font-mono font-medium text-white uppercase transition-colors"
                style={{ letterSpacing: "0.48px" }}
              >
                {t(k.applyNow)}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Summary (right-aligned) ─── */}
      {job.summary && (
        <section className="px-6 py-12">
          <div className="mx-auto flex max-w-[1100px] justify-end">
            <div className="w-full md:w-[60%]">
              <div>
                <RenderLexical content={job.summary} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── Role grid (highlights) ─── */}
      {job.highlights && job.highlights.length > 0 && (
        <section className="px-6 py-16">
          <div className="mx-auto max-w-[1100px]">
            <div>
              <p
                className="type-label-md text-foreground font-mono font-medium uppercase"
                style={{ letterSpacing: "0.48px" }}
              >
                {t(k.yourRoleLabel as never)}
              </p>
              <h2 className="type-display-lg text-foreground mt-4 max-w-[400px] text-4xl leading-[0.9] font-normal sm:text-5xl">
                {t(k.yourRoleHeading as never)}
              </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-x-16 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {job.highlights.map((item, index) => (
                <div key={item.id || index}>
                  <div>
                    <h3 className="type-title-lg text-foreground mb-3 font-normal">
                      {item.title}
                    </h3>
                    <p className="text-foreground text-base leading-[1.6]">
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA + Description (right-aligned) ─── */}
      <section className="px-6 py-12">
        <div className="mx-auto flex max-w-[1100px] justify-end">
          <div className="w-full md:w-[60%]">
            {/* CTA interstitial */}
            <div>
              <div className="mb-12">
                <p className="type-title-md text-foreground mb-4">
                  {t("didWePiqueInterest" as never)}
                </p>
                <a
                  href={applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="type-label-md bg-aubergine-800 hover:bg-aubergine-900 inline-flex items-center justify-center rounded-lg px-4 py-2 font-mono font-medium text-white uppercase transition-colors"
                  style={{ letterSpacing: "0.48px" }}
                >
                  {t(k.applyNow)}
                </a>
              </div>
            </div>

            {/* Description body */}
            {job.description && (
              <div>
                <RenderLexical content={job.description} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── Dark section: FAQ + Bento + Open roles ─── */}
      <div className="bg-foreground">
        {/* FAQ */}
        {initialFaqs && initialFaqs.length > 0 && (
          // currentPage is the per-job slug so it never matches a CMS `pages`
          // value ("careers" lives on /careers, not on each job page) — these
          // FAQs render visibly here but the FAQPage schema stays on /careers
          // to avoid duplicating Q&A markup across every job posting.
          <FaqSection
            heading={t("faqHeading" as never)}
            items={initialFaqs}
            variant="dark"
            linkLabel={null}
            currentPage={`careers/${job.slug}`}
          />
        )}

        {/* Bento blocks */}
        <section className="px-6 pb-20">
          <div className="mx-auto flex max-w-[1100px] flex-col gap-6">
            {/* Row 1: text card + Zurich photo */}
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="md:w-[57%]">
                <div className="bg-background flex h-full min-h-[400px] flex-col justify-between rounded-xl p-8 md:min-h-[480px] md:p-12">
                  <h3 className="type-display-lg text-foreground max-w-[480px] text-3xl leading-[1] font-normal sm:text-4xl">
                    {t(k.bentoHeadline as never)}
                  </h3>
                  <p className="type-title-md text-foreground/70 mt-auto pt-8">
                    {t(k.bentoSubtitle as never)}
                  </p>
                </div>
              </div>
              <div className="md:w-[43%]">
                <div className="relative h-full min-h-[300px] overflow-hidden rounded-xl md:min-h-[480px]">
                  <Image
                    src={blobMediaUrl("/images/about/zurich.jpg")}
                    alt={t(k.bentoZurich as never)}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                    <p className="text-lg font-normal text-white">
                      {t(k.bentoZurich as never)}
                    </p>
                    <p className="mt-1 text-sm text-white/70">
                      {t(k.bentoZurichCaption as never)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: team photo + backed by card */}
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="md:w-[43%]">
                <div className="relative h-full min-h-[300px] overflow-hidden rounded-xl md:min-h-[480px]">
                  <Image
                    src={blobMediaUrl("/images/misc/careers-hero.png")}
                    alt={t(k.heroImageAlt)}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
              <div className="md:w-[57%]">
                <div className="bg-light-blue-300 flex h-full min-h-[400px] flex-col justify-between rounded-xl p-8 md:min-h-[480px] md:p-12">
                  <h3 className="type-display-lg text-foreground max-w-[480px] text-3xl leading-[1] font-normal sm:text-4xl">
                    {t(k.backedByLabel)}
                  </h3>
                  <p className="type-title-md text-foreground/70 mt-auto pt-8">
                    {t(k.backedByBody)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open roles */}
        {otherJobs.length > 0 && (
          <section className="px-6 pb-20">
            <div className="mx-auto max-w-[1100px]">
              <div>
                <p
                  className="type-label-md mb-8 font-mono font-medium text-white/50 uppercase"
                  style={{ letterSpacing: "0.48px" }}
                >
                  {t(k.openRolesLabel)}
                </p>
              </div>

              <div className="flex flex-col">
                {otherJobs.map((other) => (
                  <div key={other.id}>
                    <a
                      href={lp(`/careers/${jobSlug(other)}`)}
                      className="group flex items-center justify-between border-t border-white/20 py-6 last:border-b last:border-white/20"
                    >
                      <div>
                        <OtherJobTitle
                          id={other.id}
                          title={other.title}
                          editLocale={editLocale}
                          className="text-lg font-normal text-white transition-colors group-hover:text-white/80 md:text-xl"
                        />
                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-sm text-white/60">
                          {other.type && (
                            <span>
                              {other.type === "Full-time"
                                ? t("jobTypeFullTime" as never)
                                : other.type === "Part-time"
                                  ? t("jobTypePartTime" as never)
                                  : other.type}
                            </span>
                          )}
                          {other.type && other.location && <span>·</span>}
                          {other.location && <span>{other.location}</span>}
                        </div>
                      </div>
                      <span className="ml-4 flex-shrink-0 text-white/40 transition-colors group-hover:text-white">
                        <ArrowRightIcon />
                      </span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  )
}
